const { createSelector } = require('reselect');
const moment = require('moment');
const filterUnique = require('utilities/filterUnique');
const {
    INITIAL_AMOUNT,
    INITIAL_PERIOD
} = require('../constants');

const getFund = (_, props) => props.fund;

const getGrowthAmount = state => state.brokerInvestmentFundView.growthAmount;

const getGrowthPeriod = state => state.brokerInvestmentFundView.growthPeriod;

const getProfitabilityMinYear = state => state.brokerInvestmentFundView.profitabilityMinYear;

const getAnnualReturnData = createSelector(
    [getFund],
    fund => {
        if (!!fund.historicalPerformanceSeries) {
            return fund.historicalPerformanceSeries[0].return
            .map(r => ({ ...r, year: moment(r.date).year() }))
            .reverse()
        }
        return null;
    }
);

const getAssetAllocationsBenchmark = createSelector(
    [getFund],
    fund => {
        if (!fund) return null
        if (!fund.benchmark) return null
        if (!fund.benchmark[0]) return null
        if (!fund.benchmark[0].portfolios) return null
        if (!fund.benchmark[0].portfolios[0]) return null
        if (fund.benchmark[0].portfolios[0].assetAllocations) {
            return fund.benchmark[0]
                .portfolios[0]
                .assetAllocations
                .find(p => p.salePosition === 'N')
                .breakdownValues
        } else {
            if (!fund.portfolios[0]) return null
            return fund.portfolios[0]
                .assetAllocations
                .find(p => p.salePosition === 'N')
                .breakdownValues
        }
    }
);

const getAssetAllocations = createSelector(
    [getFund, getAssetAllocationsBenchmark],
    fund => {
        if(fund.portfolios) {
            const assetAllocations = fund.portfolios[0].assetAllocations;
            const uniqueTypes = assetAllocations
                .reduce((sum, mcb) => sum.concat(mcb.breakdownValues.map(bv => bv.type)), [])
                .filter(filterUnique)
                .sort();

                const returnValues =  uniqueTypes.map((type) => {
                    return (
                        {
                            long: assetAllocations.find(aa => aa.salePosition === 'L').breakdownValues.find(bv => bv.type === String(type)).value || 0,
                            type,
                            short: assetAllocations.find(aa => aa.salePosition === 'S').breakdownValues.find(bv => bv.type === String(type)).value || 0,
                            net: assetAllocations.find(aa => aa.salePosition === 'N').breakdownValues.find(bv => bv.type === String(type)).value || 0,
                            cat: 0,
                            sum: assetAllocations.reduce((sum, aa) => sum + (aa.breakdownValues.find(bv => bv.type === String(type)).value || 0), 0),
                        }
                    )
                })

                return returnValues;
        }
    }
);

const getGrowthBenchmark = createSelector(
    [getFund],
    fund => !!fund.benchmark
        ? fund.benchmark.find(b => b.type === 'CategoryPrimaryIndex' && b.growthOf10K) ||
            fund.benchmark[0]
        : null
);

const getGrowthData = createSelector(
    [getFund, getGrowthBenchmark, getGrowthAmount, getGrowthPeriod],
    (fund, benchmark, amount, period) => {

        const initialAmount = amount ? amount : INITIAL_AMOUNT;
        const validateStatePeriod = period ? period : INITIAL_PERIOD;
        let listPointForGraphic;

        if (fund.growthOf10K) {
            const listPrice = fund.growthOf10K[0].historyDetails
            .filter((pd) =>
            validateStatePeriod
                    ? moment(pd.endDate).isAfter(
                        moment().subtract(validateStatePeriod, "years")
                    )
                    : true
            );

            const startPoint = {
                benchmark: initialAmount,
                date: listPrice[0].endDate,
                value: listPrice[0].value,
                growth: initialAmount
            }

            const sharesAmount = initialAmount / startPoint.value;

            listPointForGraphic = fund.growthOf10K[0].historyDetails
                .filter((pd) =>
                    validateStatePeriod
                        ? moment(pd.endDate).isAfter(
                            moment().subtract(validateStatePeriod, "years")
                        )
                        : true
                )
                .map((dataPoint) => {
                    return {
                        ...dataPoint,
                        date: dataPoint.endDate,
                        growth: sharesAmount * dataPoint.value,
                        benchmark: (dataPoint.value * initialAmount) / startPoint.value,
                    };
                });

            listPointForGraphic[0] = startPoint;
        }

        return listPointForGraphic;
    }
);

const getMarketCapitalBreakdownData = createSelector(
    [getFund],
    fund => {
        if (fund.portfolios) {
            const marketCapitalBreakdown = fund.portfolios[0].marketCapitalBreakdown;
            if (marketCapitalBreakdown) {
                const uniqueTypes = marketCapitalBreakdown
                .reduce((sum, mcb) => sum.concat(mcb.breakdownValues.map(bv => bv.type)), [])
                .filter(filterUnique)
                .sort();

                return uniqueTypes
                    .map(type => ({
                        type,
                        long: marketCapitalBreakdown.find(mcb => mcb.salePosition === 'L').breakdownValues.find(bv => bv.type === String(type)).value || 0,
                        short: marketCapitalBreakdown.find(mcb => mcb.salePosition === 'S').breakdownValues.find(bv => bv.type === String(type)).value || 0,
                        net: marketCapitalBreakdown.find(mcb => mcb.salePosition === 'N').breakdownValues.find(bv => bv.type === String(type)).value || 0,
                        cat: 0,
                        sum: marketCapitalBreakdown.reduce((sum, mcb) => sum + (mcb.breakdownValues.find(bv => bv.type === String(type)).value || 0), 0),
                    }))
                    .filter(row => row.sum > 0)
            } else {
                return null
            }
        }
    }
);

const getPortfolioHoldings = createSelector(
    [getFund],
    fund => fund.portfolios && fund.portfolios[0].portfolioHoldings
);

const getProfitabilityBenchmark = createSelector(
    [getFund],
    fund => {
        if(!!fund.benchmark) {
            return fund.benchmark.find(b =>
                b.historicalPerformanceSeries && b.historicalPerformanceSeries.some(hps =>
                    hps.frequency === 'Y'
                )
            )
        }
        return null;
    }
);

const getTrailingPerformance = createSelector(
    [getFund],
    fund => !!fund.trailingPerformance
        ? fund.trailingPerformance.filter(f => f.currencyId === 'EUR')[0]
        : null
);

const getTrailingPerformanceBenchmark = createSelector(
    [getFund],
    fund => fund.benchmark[0]
);

const getHistoricalPerformanceSeries = createSelector(
    [getFund],
    fund => {
        if (!!fund.historicalPerformanceSeries) {
            const historicalPerformanceSeries = fund.historicalPerformanceSeries.find(hps => hps.frequency === 'Y');
            if (!!historicalPerformanceSeries) {
                return !!historicalPerformanceSeries.return && historicalPerformanceSeries.return
            }
        }
        return null
    }
);

const getProfitabilityData = createSelector(
    [getFund, getHistoricalPerformanceSeries, getProfitabilityBenchmark, getProfitabilityMinYear],
    (fund, historicalPerformanceSeries, benchmark, minYear) => {
        if (historicalPerformanceSeries && benchmark) {
            return (historicalPerformanceSeries
            .map(r => ({ ...r, benchmark: benchmark.historicalPerformanceSeries.find(hps => hps.frequency === 'Y').return.find(b => b.date === r.date).value }))
            .filter(r => minYear ? moment(r.date).year() >= minYear : true)
            .sort((a, b) => moment(a.date).year() - moment(b.date).year())
        )}
        return null;
    }
);

const getRiskStatistics = createSelector(
    [getFund],
    fund => fund.riskStatistics[0]
);

const getCountryExposureBenchmark = createSelector(
    [getFund],
    fund => fund.benchmark
        ? fund
        .benchmark
        .find(b => b.portfolios && b.portfolios[0].countryExposure)
        .portfolios[0]
        .countryExposure
        .find(ce => ce.salePosition === 'N')
        .breakdownValues : null
);

const getCountryExposureData = createSelector(
    [getFund, getCountryExposureBenchmark],
    (fund, benchmark) => {
        if (fund.portfolios) {
            const countryExposure =
                !!fund.portfolios[0].countryExposure &&
                fund.portfolios[0].countryExposure.find(
                    (ce) => ce.salePosition === "N"
                ).breakdownValues;
            if (countryExposure) {
                return (
                    countryExposure.sort((a, b) => parseFloat(b.value) - parseFloat(a.value)).slice(0, 10).map(bv => ({
                        ...bv, benchmark: benchmark.find(b => b.type === bv.type) !== undefined ? benchmark.find(b => b.type === bv.type).value : ''
                    }))
                )
            }
            return null;
        }
        return null;
    }
);

const getGlobalStockBenchmark = createSelector(
    [getFund],
    fund => {
        const stockSectorBreakdown = fund.benchmark
        ? fund.benchmark.find(b => b.portfolios && b.portfolios[0].globalStockSectorBreakdown)
        : null;
        if (stockSectorBreakdown) {
            return stockSectorBreakdown
            .portfolios[0]
            .globalStockSectorBreakdown
            .find(gssb => gssb.salePosition === 'N')
            .breakdownValues
        }

        return null
    }
);

const getGlobalStockData = createSelector(
    [getFund, getGlobalStockBenchmark],
    (fund, benchmark) => {
        if (fund.portfolios) {
            const globalStockSectorBreakdown = fund.portfolios[0].globalStockSectorBreakdown;
            if (globalStockSectorBreakdown && benchmark !== null) {
                return (
                    globalStockSectorBreakdown.find(gssb => gssb.salePosition === 'N')
                    .breakdownValues
                    .map(bv => {
                        const benchmarkValue = benchmark.find(b => b.type === bv.type);
                        return { ...bv, benchmark: benchmarkValue ? benchmarkValue.value : null }
                    }))
            }
            return null
        }
    }
);

module.exports = {
    getAnnualReturnData,
    getAssetAllocations,
    getAssetAllocationsBenchmark,
    getCountryExposureData,
    getGlobalStockData,
    getGrowthBenchmark,
    getGrowthData,
    getMarketCapitalBreakdownData,
    getPortfolioHoldings,
    getProfitabilityBenchmark,
    getProfitabilityData,
    getRiskStatistics,
    getTrailingPerformance,
    getTrailingPerformanceBenchmark,
};
